import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { DEFAULT_ROLES, NATIONALITIES } from '@cls/shared';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Agency { code: string; name: string; }

interface Props {
  onClose: () => void;
  initial?: {
    id?: string;
    name?: string;
    role?: string;
    roles?: string[];
    phone?: string;
    email?: string | null;
    postcode?: string;
    address?: string | null;
    status?: string;
    onSiteUntil?: string | null;
    companyRef?: string | null;
    payrollRef?: string | null;
    agencyCode?: string | null;
    rtwStatus?: string | null;
    rtwExpiry?: string | null;
    nationality?: string | null;
    emergencyContact?: string | null;
    core?: boolean;
    rating?: number | null;
    photo?: string | null;
  };
}

type Rtw = '' | 'uk' | 'iltr' | 'visa';

export function AddWorkerModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;

  const { values: roles } = useReferenceList('roles', DEFAULT_ROLES);
  const { values: statuses } = useReferenceList('statuses', ['available', 'deployed', 'unavailable', 'dnu']);

  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState(initial?.role ?? '');
  const [additionalRoles, setAdditionalRoles] = useState<string[]>(initial?.roles ?? []);
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [postcode, setPostcode] = useState(initial?.postcode ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [status, setStatus] = useState(initial?.status ?? 'available');
  const [onSiteUntil, setOnSiteUntil] = useState(initial?.onSiteUntil?.slice(0, 10) ?? '');
  const [companyRef, setCompanyRef] = useState(initial?.companyRef ?? '');
  const [payrollRef, setPayrollRef] = useState(initial?.payrollRef ?? '');
  const [agency, setAgency] = useState(initial?.agencyCode ?? '');
  const [rtw, setRtw] = useState<Rtw>((initial?.rtwStatus as Rtw) ?? '');
  const [rtwExpiry, setRtwExpiry] = useState(initial?.rtwExpiry?.slice(0, 10) ?? '');
  const [nationality, setNationality] = useState(initial?.nationality ?? '');
  const [emergency, setEmergency] = useState(initial?.emergencyContact ?? '');
  const [core, setCore] = useState(initial?.core ?? false);
  const [rating, setRating] = useState<number>(initial?.rating ?? 0);
  const [sigPaste, setSigPaste] = useState('');
  const [photo, setPhoto] = useState<string | null>(initial?.photo ?? null);
  const [photoUrlInput, setPhotoUrlInput] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoFileRef = useRef<HTMLInputElement | null>(null);

  const { data: agencies = [] } = useQuery({
    queryKey: ['agencies'],
    queryFn: () => api.get<Agency[]>('/agencies').then((r) => r.data).catch(() => [] as Agency[]),
  });

  const uploadPhotoFile = async (f: File) => {
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const up = await api.post<{ url: string }>('/uploads/photo', fd);
      setPhoto(up.data.url);
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        role,
        // Always send the array (even empty) so unchecking everything actually
        // clears stale values on the worker record. Filter out the primary role
        // so it isn't double-counted in the additional list.
        roles: additionalRoles.filter((r) => r && r !== role),
        phone: phone.trim(),
        email: email.trim() || null,
        postcode: postcode.trim(),
        address: address.trim() || null,
        status,
        onSiteUntil: onSiteUntil || null,
        companyRef: companyRef.trim() || null,
        payrollRef: payrollRef.trim() || null,
        agencyCode: agency || null,
        rtwStatus: rtw || null,
        rtwExpiry: rtwExpiry || null,
        nationality: nationality || null,
        emergencyContact: emergency.trim() || null,
        core,
        rating: typeof rating === 'number' ? rating : 0,
      };
      const fullBody = { ...body, photo: photo ?? null };
      if (editing && initial?.id) {
        return api.patch(`/workers/${initial.id}`, fullBody);
      }
      return api.post('/workers', fullBody);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      toast.success(editing ? 'Worker updated' : 'Worker added', name);
      onClose();
    },
    onError: (e: any) => {
      toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again');
    },
  });

  const applySig = () => {
    const text = sigPaste;
    if (!text.trim()) return;
    const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    const phoneMatch = text.match(/(?:\+?44\s?|0)(?:\d\s?){9,10}/);
    const postcodeMatch = text.match(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i);
    if (emailMatch && !email) setEmail(emailMatch[0]);
    if (phoneMatch && !phone) setPhone(phoneMatch[0].replace(/\s/g, ''));
    if (postcodeMatch && !postcode) setPostcode(postcodeMatch[0].toUpperCase());
    const firstLine = text.split('\n').map((l) => l.trim()).find((l) => l && /^[A-Za-z][A-Za-z .'-]+$/.test(l) && l.split(' ').length >= 2);
    if (firstLine && !name) setName(firstLine);
  };

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="modal-title" id="aw-title">{editing ? '✏ Edit Worker' : '+ Add New Worker'}</div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <details id="aw-sig-details" style={{ marginBottom: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <summary style={{ cursor: 'pointer', padding: '9px 12px', fontSize: 11, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>📋 Paste email signature to auto-fill</span>
            <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>click to expand</span>
          </summary>
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)' }}>
            <textarea className="form-textarea" id="aw-sig-paste" placeholder="Paste the signature here — name, phone, email, postcode, address will be extracted automatically" style={{ minHeight: 90, fontSize: 11 }} value={sigPaste} onChange={(e) => setSigPaste(e.target.value)} onBlur={applySig} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 7, gap: 8 }}>
              <button type="button" className="btn btn-ghost btn-xs" id="aw-sig-clear" onClick={() => setSigPaste('')} title="Clear the paste box">🗑 Clear</button>
              <button type="button" className="btn btn-primary btn-xs" onClick={applySig}>Apply</button>
            </div>
          </div>
        </details>

        <div className="form-row" style={{ marginBottom: 14 }}>
          <label className="form-label">Photo <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— optional, click / drop / paste URL</span></label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              onClick={() => photoFileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) uploadPhotoFile(f); }}
              style={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                border: '2px dashed var(--border)',
                background: 'var(--card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--faint)',
                fontSize: 10,
                textAlign: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                flexShrink: 0,
                lineHeight: 1.3,
              }}
              title="Click or drop a photo"
            >
              {photo ? (
                <img src={photo} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : photoUploading ? 'Uploading…' : <span>📷<br />Click or drop<br />a photo</span>}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => photoFileRef.current?.click()}>📂 Upload</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPhoto(null); setPhotoUrlInput(''); }} disabled={!photo && !photoUrlInput}>🗑 Remove</button>
              </div>
              <input
                type="text"
                className="form-input"
                placeholder="Or paste image URL and press Enter"
                value={photoUrlInput}
                onChange={(e) => setPhotoUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (photoUrlInput.trim()) setPhoto(photoUrlInput.trim()); } }}
                style={{ width: '100%' }}
              />
            </div>
            <input
              ref={photoFileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhotoFile(f); }}
            />
          </div>
        </div>

        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Full Name</label>
            <input type="text" className="form-input" id="aw-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Primary Role</label>
            <SearchableSelect
              id="aw-role"
              value={role}
              onChange={setRole}
              placeholder="— Select role —"
              options={roles.map((r) => ({ value: r, label: r }))}
            />
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Additional Roles (tick all that apply)</label>
          <div id="aw-roles-checks" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 5, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
            {/*
              Render the union of (Settings list) + (any roles already on the
              worker) so legacy values that aren't in /settings/roles still
              appear here as checked checkboxes the user can uncheck. Without
              this, an old role like "Logistics Operative" sitting in
              roles[] becomes invisible and impossible to remove.
            */}
            {Array.from(new Set([...roles, ...additionalRoles])).map((r) => {
              const orphan = !roles.includes(r);
              return (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer' }} title={orphan ? 'Not in Settings → Worker Roles list' : undefined}>
                  <input
                    type="checkbox"
                    checked={additionalRoles.includes(r)}
                    onChange={(e) => {
                      if (e.target.checked) setAdditionalRoles([...additionalRoles, r]);
                      else setAdditionalRoles(additionalRoles.filter((x) => x !== r));
                    }}
                    style={{ accentColor: 'var(--accent)' }}
                  />
                  <span style={orphan ? { color: 'var(--warn)', fontStyle: 'italic' } : undefined}>{r}{orphan ? ' ⚠' : ''}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Mobile (WhatsApp) <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— required, 11 digits</span></label>
            <input type="tel" className="form-input" id="aw-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" id="aw-email" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Home Postcode</label>
            <input type="text" className="form-input" id="aw-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Home Address</label>
            <input type="text" className="form-input" id="aw-address" value={address ?? ''} onChange={(e) => setAddress(e.target.value)} />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Status</label>
            <SearchableSelect
              id="aw-status"
              value={status}
              onChange={setStatus}
              placeholder="— Select status —"
              options={statuses.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            />
          </div>
          <div className="form-row">
            <label className="form-label">On Site Until (if deployed/unavailable)</label>
            <input type="date" className="form-input" id="aw-onsiteuntil" value={onSiteUntil ?? ''} onChange={(e) => setOnSiteUntil(e.target.value)} />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Company Reference</label>
            <input type="text" className="form-input" id="aw-companyref" placeholder="e.g. CLS-W014" value={companyRef ?? ''} onChange={(e) => setCompanyRef(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Payroll Reference</label>
            <input type="text" className="form-input" id="aw-payrollref" placeholder="e.g. PAY-2024-014" value={payrollRef ?? ''} onChange={(e) => setPayrollRef(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Agency</label>
            <SearchableSelect
              id="aw-agency"
              value={agency ?? ''}
              onChange={setAgency}
              placeholder="— No Agency (Direct) —"
              options={[
                { value: '', label: '— No Agency (Direct) —' },
                ...agencies.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` })),
              ]}
            />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Right to Work (RTW)</label>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}><input type="radio" name="aw-rtw" value="uk" checked={rtw === 'uk'} onChange={() => setRtw('uk')} style={{ accentColor: 'var(--accent)' }} /> UK Citizen</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}><input type="radio" name="aw-rtw" value="iltr" checked={rtw === 'iltr'} onChange={() => setRtw('iltr')} style={{ accentColor: 'var(--accent)' }} /> ILTR</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}><input type="radio" name="aw-rtw" value="visa" checked={rtw === 'visa'} onChange={() => setRtw('visa')} style={{ accentColor: 'var(--accent)' }} /> Visa</label>
                <button type="button" style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 10, marginLeft: 'auto', padding: 0 }} onClick={() => { setRtw(''); setRtwExpiry(''); }}>Clear</button>
              </div>
              {rtw === 'visa' ? (
                <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 11, color: 'var(--muted)', minWidth: 75 }}>Visa expiry</label>
                  <input type="date" className="form-input" id="aw-rtw-expiry" value={rtwExpiry} onChange={(e) => setRtwExpiry(e.target.value)} style={{ flex: 1 }} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="form-row">
            <label className="form-label">Nationality</label>
            <SearchableSelect
              id="aw-nationality"
              value={nationality ?? ''}
              onChange={setNationality}
              placeholder="— Select nationality —"
              options={[
                { value: '', label: '— Select nationality —' },
                ...NATIONALITIES.map((n) => ({ value: n, label: n })),
              ]}
            />
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Emergency Contact</label>
          <input type="text" className="form-input" id="aw-emergency" value={emergency ?? ''} onChange={(e) => setEmergency(e.target.value)} />
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Core Business Person</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
              <input type="checkbox" id="aw-core" checked={core} onChange={(e) => setCore(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 14, height: 14 }} />
              <span>Part of CLS core team</span>
            </label>
          </div>
          <div className="form-row">
            <label className="form-label">Rating</label>
            <div id="aw-rating" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', userSelect: 'none' }}>
              <div style={{ display: 'flex', gap: 3, fontSize: 20, lineHeight: 1 }} id="aw-rating-stars">
                {[1, 2, 3, 4, 5].map((v) => (
                  <span key={v} className="aw-star" data-v={v} onClick={() => setRating(v)} style={{ cursor: 'pointer', color: v <= rating ? 'var(--accent)' : 'var(--faint)', transition: 'color .1s' }}>★</span>
                ))}
              </div>
              <span id="aw-rating-label" style={{ fontSize: 13, fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)', minWidth: 80 }}>
                {rating === 0 ? 'Not rated' : `${rating} star${rating !== 1 ? 's' : ''}`}
              </span>
              <button type="button" id="aw-rating-clear" onClick={() => setRating(0)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, marginLeft: 'auto', padding: '4px 10px', borderRadius: 5 }}>Clear</button>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save Worker'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
